import { useSearchParams } from "react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdClose } from "react-icons/io";
import { useIsMobile } from "~/hook/use-mobile";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { selectedFilterDataType } from "~/interface/kirtan-lyrics/kirtan-lyrics";
import {
  addSelectedBhav,
  addSelectedKBook,
  addSelectedKPlace,
  addSelectedKPrasang,
  addSelectedRaagDhal,
  addSelectedRachayta,
  removeSelectedBhav,
  removeSelectedKBook,
  removeSelectedKPlace,
  removeSelectedKPrasang,
  removeSelectedRaagDhal,
  removeSelectedRachayta,
  setSelectedBhav,
  setSelectedKBook,
  setSelectedKPlace,
  setSelectedKPrasang,
  setSelectedRaagDhal,
  setSelectedRachayta,
} from "~/lib/store/slice/kirtanLyricsSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { SheetClose } from "../ui/sheet";
import KirtanFilterAccordion from "./KirtanFilterAccordion";
import KirtanLyricsFilterMobile from "./KirtanLyricsFilterMobile";

interface KirtanLyricsFilterProps {
  onFilterApplied?: () => void;
  // Props for SSR fallback when Redux is empty
  bhav?: selectedFilterDataType[];
  raagDhaal?: selectedFilterDataType[];
  rachayta?: selectedFilterDataType[];
  k_book?: selectedFilterDataType[];
  k_place?: selectedFilterDataType[];
  k_prasang?: selectedFilterDataType[];
  selectedBhav?: selectedFilterDataType[];
  selectedRaagDhal?: selectedFilterDataType[];
  selectedRachayta?: selectedFilterDataType[];
  selectedKBook?: selectedFilterDataType[];
  selectedKPlace?: selectedFilterDataType[];
  selectedKPrasang?: selectedFilterDataType[];
}

const KirtanLyricsFilter = ({
  onFilterApplied,
  bhav: bhavProp = [],
  raagDhaal: raagDhaalProp = [],
  rachayta: rachayataProp = [],
  k_book: kBookProp = [],
  k_place: kPlaceProp = [],
  k_prasang: kPrasangProp = [],
  selectedBhav: selectedBhavProp = [],
  selectedRaagDhal: selectedRaagDhalProp = [],
  selectedRachayta: selectedRachayataProp = [],
  selectedKBook: selectedKBookProp = [],
  selectedKPlace: selectedKPlaceProp = [],
  selectedKPrasang: selectedKPrasangProp = [],
}: KirtanLyricsFilterProps) => {
  const reduxState = useAppSelector((state) => state.kirtanLyrics);

  // Use Redux data if available, fallback to props for SSR
  const bhav = reduxState.bhav?.length > 0 ? reduxState.bhav : bhavProp;
  const raagDhaal = reduxState.raagDhaal?.length > 0 ? reduxState.raagDhaal : raagDhaalProp;
  const rachayta = reduxState.rachayta?.length > 0 ? reduxState.rachayta : rachayataProp;
  const k_book = reduxState.k_book?.length > 0 ? reduxState.k_book : kBookProp;
  const k_place = reduxState.k_place?.length > 0 ? reduxState.k_place : kPlaceProp;
  const k_prasang = reduxState.k_prasang?.length > 0 ? reduxState.k_prasang : kPrasangProp;
  const selectedBhav = reduxState.selectedBhav?.length > 0 ? reduxState.selectedBhav : selectedBhavProp;
  const selectedRaagDhal = reduxState.selectedRaagDhal?.length > 0 ? reduxState.selectedRaagDhal : selectedRaagDhalProp;
  const selectedRachayta = reduxState.selectedRachayta?.length > 0 ? reduxState.selectedRachayta : selectedRachayataProp;
  const selectedKBook = reduxState.selectedKBook?.length > 0 ? reduxState.selectedKBook : selectedKBookProp;
  const selectedKPlace = reduxState.selectedKPlace?.length > 0 ? reduxState.selectedKPlace : selectedKPlaceProp;
  const selectedKPrasang = reduxState.selectedKPrasang?.length > 0 ? reduxState.selectedKPrasang : selectedKPrasangProp;
  const { medium, normalInter } = useFontStyle();

  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const { navigate } = useLocale();

  const { t } = useTranslation();

  const [, setSearchParams] = useSearchParams();
  const [clear, setClear] = useState(false);

  // Temporary state for mobile - stores pending selections until "Apply" is clicked
  const [tempSelectedRachayta, setTempSelectedRachayta] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedRaagDhal, setTempSelectedRaagDhal] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedBhav, setTempSelectedBhav] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedKBook, setTempSelectedKBook] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedKPlace, setTempSelectedKPlace] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedKPrasang, setTempSelectedKPrasang] = useState<selectedFilterDataType[]>([]);

  // Initialize temp state when sheet opens or when actual selections change
  // This ensures temp state syncs with URL changes (e.g., browser back/forward, URL edits)
  useEffect(() => {
    if (isMobile) {
      setTempSelectedRachayta(selectedRachayta);
      setTempSelectedRaagDhal(selectedRaagDhal);
      setTempSelectedBhav(selectedBhav);
      setTempSelectedKBook(selectedKBook);
      setTempSelectedKPlace(selectedKPlace);
      setTempSelectedKPrasang(selectedKPrasang);
    }
  }, [isMobile, selectedRachayta, selectedRaagDhal, selectedBhav, selectedKBook, selectedKPlace, selectedKPrasang]);

  const handleSelecteRachayta = (rachayta: { id: number; text: string }) => {
    setClear(false);
    if (isMobile) {
      // Mobile: Update temp state only
      handleMobileSelection(rachayta, tempSelectedRachayta, setTempSelectedRachayta);
    } else {
      // Desktop: Apply instantly
      handleSelection(rachayta, selectedRachayta, addSelectedRachayta, removeSelectedRachayta, "rachayta");
    }
  };

  const handleSelecteRaagDhal = (raagDhal: { id: number; text: string }) => {
    setClear(false);
    if (isMobile) {
      // Mobile: Update temp state only
      handleMobileSelection(raagDhal, tempSelectedRaagDhal, setTempSelectedRaagDhal);
    } else {
      // Desktop: Apply instantly
      handleSelection(raagDhal, selectedRaagDhal, addSelectedRaagDhal, removeSelectedRaagDhal, "raagdhal");
    }
  };

  const handleSelecteBhav = (bhav: { id: number; text: string }) => {
    setClear(false);
    if (isMobile) {
      // Mobile: Update temp state only
      handleMobileSelection(bhav, tempSelectedBhav, setTempSelectedBhav);
    } else {
      // Desktop: Apply instantly
      handleSelection(bhav, selectedBhav, addSelectedBhav, removeSelectedBhav, "bhav");
    }
  };

  const handleSelecteKBook = (kBook: { id: number; text: string }) => {
    setClear(false);
    if (isMobile) {
      // Mobile: Update temp state only
      handleMobileSelection(kBook, tempSelectedKBook, setTempSelectedKBook);
    } else {
      // Desktop: Apply instantly
      handleSelection(kBook, selectedKBook, addSelectedKBook, removeSelectedKBook, "k_book");
    }
  };

  const handleSelecteKPlace = (kPlace: { id: number; text: string }) => {
    setClear(false);
    if (isMobile) {
      // Mobile: Update temp state only
      handleMobileSelection(kPlace, tempSelectedKPlace, setTempSelectedKPlace);
    } else {
      // Desktop: Apply instantly
      handleSelection(kPlace, selectedKPlace, addSelectedKPlace, removeSelectedKPlace, "k_place");
    }
  };

  const handleSelecteKPrasang = (kPrasang: { id: number; text: string }) => {
    setClear(false);
    if (isMobile) {
      // Mobile: Update temp state only
      handleMobileSelection(kPrasang, tempSelectedKPrasang, setTempSelectedKPrasang);
    } else {
      // Desktop: Apply instantly
      handleSelection(kPrasang, selectedKPrasang, addSelectedKPrasang, removeSelectedKPrasang, "k_prasang");
    }
  };

  // Mobile: Toggle selection in temporary state without applying
  const handleMobileSelection = (
    item: { id: number; text: string },
    currentItems: selectedFilterDataType[],
    setItems: React.Dispatch<React.SetStateAction<selectedFilterDataType[]>>,
  ) => {
    const isAlreadySelected = currentItems.some((i) => i.id === item.id);

    if (isAlreadySelected) {
      setItems(currentItems.filter((i) => i.id !== item.id));
    } else {
      setItems([...currentItems, item]);
    }
  };

  const handleSelection = (
    item: { id: number; text: string },
    selectedItems: selectedFilterDataType[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addAction: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    removeAction: any,
    paramKey: string,
  ) => {
    let newItems = [...selectedItems];
    const isAlreadySelected = newItems.some((i) => i.id === item.id);

    if (isAlreadySelected) {
      newItems = newItems.filter((i) => i.id !== item.id);
      dispatch(removeAction(item));
    } else {
      newItems.push(item);
      dispatch(addAction(item));
    }

    // Build the new params - preserve all existing filters and update current one
    const newParams = new URLSearchParams();

    // Add all existing filters from Redux state
    if (selectedRachayta.length > 0) {
      newParams.set(
        "rachayta",
        selectedRachayta
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    if (selectedRaagDhal.length > 0) {
      newParams.set(
        "raagdhal",
        selectedRaagDhal
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    if (selectedBhav.length > 0) {
      newParams.set(
        "bhav",
        selectedBhav
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    if (selectedKBook.length > 0) {
      newParams.set(
        "k_book",
        selectedKBook
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    if (selectedKPlace.length > 0) {
      newParams.set(
        "k_place",
        selectedKPlace
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    if (selectedKPrasang.length > 0) {
      newParams.set(
        "k_prasang",
        selectedKPrasang
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // Now update the current filter with new items
    if (newItems.length > 0) {
      newParams.set(
        paramKey,
        newItems
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    } else {
      // Remove the filter if it's empty
      newParams.delete(paramKey);
    }

    // Update search params
    setSearchParams(newParams, { replace: true });

    // Remove kirtanId from URL when filter is applied
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("kirtanId");
      window.history.replaceState(null, "", url.toString());
      window.dispatchEvent(new CustomEvent("kirtanIdChanged", { detail: { kirtanId: null } }));
    }

    // Call callback to close detail panel
    if (onFilterApplied) {
      onFilterApplied();
    }

    if (navigate) {
      navigate(`/kirtan-lyrics?${newParams.toString()}`, { replace: true });
    }
  };

  const handleClearAll = () => {
    setClear(true);

    if (isMobile) {
      // Mobile: Clear temp state only
      setTempSelectedRachayta([]);
      setTempSelectedRaagDhal([]);
      setTempSelectedBhav([]);
      setTempSelectedKBook([]);
      setTempSelectedKPlace([]);
      setTempSelectedKPrasang([]);
    } else {
      // Desktop: Clear and apply instantly
      dispatch(setSelectedRachayta([]));
      dispatch(setSelectedRaagDhal([]));
      dispatch(setSelectedBhav([]));
      dispatch(setSelectedKBook([]));
      dispatch(setSelectedKPlace([]));
      dispatch(setSelectedKPrasang([]));

      // Build new params without any filters
      const newParams = new URLSearchParams();

      // Update search params
      setSearchParams(newParams, { replace: true });

      // Remove kirtanId from URL when filters are cleared
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        url.searchParams.delete("kirtanId");
        window.history.replaceState(null, "", url.toString());
        window.dispatchEvent(new CustomEvent("kirtanIdChanged", { detail: { kirtanId: null } }));
      }

      // Call callback to close detail panel
      if (onFilterApplied) {
        onFilterApplied();
      }

      if (navigate) {
        navigate(`/kirtan-lyrics`, { replace: true });
      }
    }
  };

  // Mobile: Apply all temporary selections when "Apply" button is clicked
  const handleApplyFilters = () => {
    // Update Redux state with temp selections
    dispatch(setSelectedRachayta(tempSelectedRachayta));
    dispatch(setSelectedRaagDhal(tempSelectedRaagDhal));
    dispatch(setSelectedBhav(tempSelectedBhav));
    dispatch(setSelectedKBook(tempSelectedKBook));
    dispatch(setSelectedKPlace(tempSelectedKPlace));
    dispatch(setSelectedKPrasang(tempSelectedKPrasang));

    // Build the new params - start fresh
    const newParams = new URLSearchParams();

    // Rachayta
    if (tempSelectedRachayta.length > 0) {
      newParams.set(
        "rachayta",
        tempSelectedRachayta
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // Raag Dhal
    if (tempSelectedRaagDhal.length > 0) {
      newParams.set(
        "raagdhal",
        tempSelectedRaagDhal
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // Bhav
    if (tempSelectedBhav.length > 0) {
      newParams.set(
        "bhav",
        tempSelectedBhav
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // K Book
    if (tempSelectedKBook.length > 0) {
      newParams.set(
        "k_book",
        tempSelectedKBook
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // K Place
    if (tempSelectedKPlace.length > 0) {
      newParams.set(
        "k_place",
        tempSelectedKPlace
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // K Prasang
    if (tempSelectedKPrasang.length > 0) {
      newParams.set(
        "k_prasang",
        tempSelectedKPrasang
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // Update search params
    setSearchParams(newParams, { replace: true });

    // Remove kirtanId from URL when filters are applied
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("kirtanId");
      window.history.replaceState(null, "", url.toString());
      window.dispatchEvent(new CustomEvent("kirtanIdChanged", { detail: { kirtanId: null } }));
    }

    // Call callback to close detail panel
    if (onFilterApplied) {
      onFilterApplied();
    }

    if (navigate) {
      navigate(`kirtan-lyrics?${newParams.toString()}`, { replace: true });
    }
  };

  return (
    <>
      {isMobile ? (
        <KirtanLyricsFilterMobile onFilterApplied={onFilterApplied} />
      ) : (
        <div className="w-full h-auto flex flex-col justify-start items-center gap-1 ">
          <div className="w-full flex px-1 py-3 justify-between items-center sticky top-0 z-10 bg-primary">
            <span className={cn("body_text_l text-textColor font-medium", medium)}>{t("chopai.filter")}</span>
            <button
              type="button"
              className={cn("body_text_sm z-40 hidden lg:flex text-textColor font-normal cursor-pointer bg-transparent border-none p-0", normalInter)}
              onClick={handleClearAll}
            >
              {t(`chopai.clear_all`)}
            </button>
            <SheetClose className="flex lg:hidden">
              <IoMdClose size={20} className="text-textColor" />
            </SheetClose>
          </div>

          <div className="w-full flex flex-col justify-start items-center gap-3">
            {rachayta.length > 0 && (
              <KirtanFilterAccordion
                data={rachayta}
                title={t("kirtan-lyrics.rachayta")}
                selectedFilter={isMobile ? tempSelectedRachayta : selectedRachayta}
                setSelectedFilterId={handleSelecteRachayta}
                isClear={clear}
                setIsClear={() => setClear(false)}
                value="rachayta"
              />
            )}
            {raagDhaal.length > 0 && (
              <KirtanFilterAccordion
                data={raagDhaal}
                title={t("kirtan-lyrics.raag_dhal")}
                selectedFilter={isMobile ? tempSelectedRaagDhal : selectedRaagDhal}
                setSelectedFilterId={handleSelecteRaagDhal}
                isClear={clear}
                setIsClear={() => setClear(false)}
                value="raagdhal"
              />
            )}
            {bhav.length > 0 && (
              <KirtanFilterAccordion
                data={bhav}
                title={t("kirtan-lyrics.bhav")}
                selectedFilter={isMobile ? tempSelectedBhav : selectedBhav}
                setSelectedFilterId={handleSelecteBhav}
                isClear={clear}
                setIsClear={() => setClear(false)}
                value="bhav"
              />
            )}
            {k_book.length > 0 && (
              <KirtanFilterAccordion
                data={k_book}
                title={t("kirtan-lyrics.k_book")}
                selectedFilter={isMobile ? tempSelectedKBook : selectedKBook}
                setSelectedFilterId={handleSelecteKBook}
                isClear={clear}
                setIsClear={() => setClear(false)}
                value="k_book"
              />
            )}
            {k_place.length > 0 && (
              <KirtanFilterAccordion
                data={k_place}
                title={t("kirtan-lyrics.k_place")}
                selectedFilter={isMobile ? tempSelectedKPlace : selectedKPlace}
                setSelectedFilterId={handleSelecteKPlace}
                isClear={clear}
                setIsClear={() => setClear(false)}
                value="k_place"
              />
            )}
            {k_prasang.length > 0 && (
              <KirtanFilterAccordion
                data={k_prasang}
                title={t("kirtan-lyrics.k_prasang")}
                selectedFilter={isMobile ? tempSelectedKPrasang : selectedKPrasang}
                setSelectedFilterId={handleSelecteKPrasang}
                isClear={clear}
                setIsClear={() => setClear(false)}
                value="k_prasang"
              />
            )}
          </div>
          <div className="w-full flex justify-between items-center lg:hidden sticky bottom-0 bg-primary pt-2">
            <button
              className="w-[40%] bg-lightbackground rounded-full py-2 mx-auto flex justify-center items-center text-textColor text-16 font-inter font-normal"
              onClick={handleClearAll}
            >
              Clear all
            </button>
            <SheetClose
              className="w-[40%] bg-primaryColor rounded-full py-2 mx-auto flex justify-center items-center text-white text-16 font-inter font-normal"
              onClick={handleApplyFilters}
            >
              Apply
            </SheetClose>
          </div>
        </div>
      )}
    </>
  );
};

export default KirtanLyricsFilter;
